import { useCallback } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useOutletContext } from "react-router"
import { zodResolver } from "@hookform/resolvers/zod"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { toast } from "@/hooks/useToast"
import { ConfirmedBy } from "@/modules/patients/detail/health-information/confirmed-by"
import { GynaecologyEditableBody } from "@/modules/patients/detail/health-information/gynaecology/gynaecology-editable-body"
import { GynaecologyReadonlyBody } from "@/modules/patients/detail/health-information/gynaecology/gynaecology-readonly-body"
import { useEditMode } from "@/modules/patients/detail/hooks/useEditMode"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { GynaecologyForm, gynaecologySchema } from "@/modules/patients/schema/gynaecology-form-schema"
import { GynaecologyFragment$data } from "@/modules/patients/services/__generated__/GynaecologyFragment.graphql"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import {
  Ch_updatePatientGynaecologyInput,
  chUpdateAllPatientGynaecologyMutation$data,
} from "@/modules/patients/services/ch/__generated__/chUpdateAllPatientGynaecologyMutation.graphql"
import { Ch_verifyHealthInformationInput } from "@/modules/patients/services/ch/__generated__/chVerifyHealthInformationMutation.graphql"
import {
  De_updatePatientGynaecologyInput,
  deUpdateAllPatientGynaecologyMutation$data,
} from "@/modules/patients/services/de/__generated__/deUpdateAllPatientGynaecologyMutation.graphql"
import { De_verifyHealthInformationInput } from "@/modules/patients/services/de/__generated__/deVerifyHealthInformationMutation.graphql"
import { useUpdateAllPatientGynaecologyMutation } from "@/modules/patients/services/useUpdateAllPatientGynaecologyMutation"
import { useVerifyHealthInformation } from "@/modules/patients/services/useVerifyHealthInformation"
import { useDrawerStore } from "@/stores/useDrawerStore"

interface GynaecologyDrawerProps {
  canVerify: boolean
  verifiedBy: string
  verifiedAt: string
  appointmentId: string | null
  data: GynaecologyFragment$data
}

const GynaecologyDrawer = ({ canVerify, verifiedBy, verifiedAt, appointmentId, data }: GynaecologyDrawerProps) => {
  const isEditMode = useEditMode()
  const patientId = usePatientParam()
  const { t } = useTranslation()
  const closeDrawer = useDrawerStore((state) => state.closeDrawer)
  const country = useCountryParam()

  const [handleUpdateAllGynaecology] = useUpdateAllPatientGynaecologyMutation(country)
  const handleVerifyHealthInformation = useVerifyHealthInformation(country)
  const { patient } = useOutletContext<{ patient: PatientFragment$data }>()

  const form = useForm<GynaecologyForm>({
    resolver: zodResolver(gynaecologySchema),
    defaultValues: {
      firstPeriod: data.firstPeriod?.toString() ?? "",
      menopauseSince: data.menopauseSince?.toString() ?? "",
      weeksOfPregnancy: data.weeksOfPregnancy?.toString() ?? "",
      pregnancyStatus: data.pregnancy,
      pregnancyHistory: data.pregnancyHistory ?? "",
      patientBirthDate: patient.birthDate,
    },
  })

  const {
    formState: { isDirty },
    handleSubmit,
  } = form

  const handleSaveGynaecology = (data: GynaecologyForm) => {
    if (isDirty) {
      const submitData: De_updatePatientGynaecologyInput & Ch_updatePatientGynaecologyInput = {
        patientId: patientId,
        firstPeriodAtYear: data.firstPeriod ? parseInt(data.firstPeriod) : null,
        menopauseSinceYear: data.menopauseSince ? parseInt(data.menopauseSince) : null,
        weeksOfPregnancy: data.weeksOfPregnancy ? parseInt(data.weeksOfPregnancy) : null,
        pregnancyStatus: data.pregnancyStatus,
        pregnancyHistory: data.pregnancyHistory,
      }

      handleUpdateAllGynaecology({
        variables: {
          input: submitData,
        },
        onCompleted: (
          response: chUpdateAllPatientGynaecologyMutation$data & deUpdateAllPatientGynaecologyMutation$data,
        ) => {
          const errMessage = response.updateAllPatientGynaecology?.errors?.[0]?.message
          handleUpdateAllGynaecologyResponse(errMessage)
        },
      })
    } else closeDrawer()
  }

  const handleUpdateAllGynaecologyResponse = (errorMessage?: string) => {
    if (errorMessage) {
      toast({
        description: errorMessage,
      })
    } else {
      closeDrawer()
    }
  }

  const handleVerifyGynaecology = () => {
    const verification: De_verifyHealthInformationInput & Ch_verifyHealthInformationInput = {
      patientId,
      healthInformationType: "GYNAECOLOGY",
      appointmentId: appointmentId!,
    }

    handleVerifyHealthInformation(verification, closeDrawer)
  }

  const handleClose = useCallback(() => {
    FaroInsightsWrapper.pushEvent("healthInformation_gynaecology_close")
    closeDrawer()
  }, [closeDrawer])

  useDirtyDrawerMaker(isDirty, handleClose)

  return (
    <Drawer open onOpenChange={handleClose}>
      <DrawerContent>
        <DrawerClose className="top-2 right-2" />
        <DrawerHeader className="border-b py-2">
          <DrawerTitle
            className="animate-fade-in-bottom text-on-surface-subdued text-sm leading-10 font-medium"
            data-testid="gynaecology-drawer-title">
            {t("patient.healthInformation.gynaecology.title")}
          </DrawerTitle>
        </DrawerHeader>
        <DrawerBody className="p-0">
          <ScrollWrapper className="p-4">
            <div className="flex flex-col gap-4">
              <FormProvider {...form}>
                {isEditMode ? <GynaecologyEditableBody /> : <GynaecologyReadonlyBody dataView={data} />}
              </FormProvider>
            </div>
          </ScrollWrapper>
        </DrawerBody>
        <DrawerFooter className="justify-end gap-2 overflow-hidden">
          {verifiedBy !== "" && <ConfirmedBy verifiedBy={verifiedBy} verifiedAt={verifiedAt} />}
          <Button variant="secondary" onClick={handleClose} data-testid="gynaecology-cancel-close-button">
            {isEditMode ? t("common.button.cancel") : t("common.button.close")}
          </Button>
          {isEditMode && !canVerify && (
            <Button
              onClick={() => {
                FaroInsightsWrapper.pushEvent("healthInformation_gynaecology_save")
                handleSubmit(handleSaveGynaecology)()
              }}
              data-testid="gynaecology-save-button">
              {t("common.button.save")}
            </Button>
          )}
          {isEditMode && canVerify && (
            <Button
              onClick={() => {
                handleSubmit(handleSaveGynaecology)()
                handleVerifyGynaecology()
              }}
              data-testid="gynaecology-verify-button">
              {t("common.button.verify")}
            </Button>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

export { GynaecologyDrawer }
